import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import { API_ENDPOINTS, ROUTES } from "../config/constants";
import { useNavigate } from "react-router-dom";
import { normalizeAuthResponse } from "../utils/authResponse";
import {
    ArrowRight,
    CheckCircle2,
    KeyRound,
    Loader2,
    Mail,
    RotateCcw,
    ShieldCheck,
    Sparkles,
} from "lucide-react";

function VerifyEmail() {
    const location = useLocation();
    const email = location.state?.email || "";
    const [verificationCode, setVerificationCode] = useState("");
    const [error, setError] = useState("");
    const [verifySuccess, setVerifySuccess] = useState(false);
    const [codeSubmited, setCodeSubmited] = useState(false);
    const [resendOTP, setResendOTP] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResendingCode, setIsResendingCode] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!email) {
            navigate(ROUTES.SIGNUP);
        }
    }, [email, navigate]);

    const handleVerify = async (e) => {
        e.preventDefault();
        if (isVerifying) return;

        setError("");
        setCodeSubmited(true);

        if (!verificationCode.trim()) {
            setError("Verification code is required");
            return;
        }

        setIsVerifying(true);

        try {
            const response = await api.post(`${API_ENDPOINTS.VERIFY_EMAIL}`, {
                email,
                code: verificationCode,
            });

            const authPayload = normalizeAuthResponse(response.data);

            if (!authPayload) {
                setError("Error while verifying email");
                return;
            }

            setVerifySuccess(true);
        } catch (error) {
            if (error.response?.status === 422)
                setError(error.response?.data?.detail);
            if (error.response?.status === 400)
                setError(error.response?.data?.detail);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendVerificationOTP = async () => {
        if (isResendingCode) return;

        setIsResendingCode(true);

        try {
            const response = await api.post(
                `${API_ENDPOINTS.RESEND_VERIFICATION_CODE}`,
                { email },
            );

            if (response) {
                setResendOTP(true);
            }
        } catch (error) {
            setError("Issue in sending OTP");
        } finally {
            setIsResendingCode(false);
        }
    };

    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center bg-[#F8F8FE] px-3 py-8 sm:px-6 lg:px-8">
            <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.95fr_1.05fr]">
                <div className="hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 p-8 text-white lg:flex lg:flex-col lg:justify-between">
                    <div>
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                            <ShieldCheck size={24} aria-hidden="true" />
                        </div>
                        <h1 className="mt-8 text-3xl font-bold leading-tight">
                            One quick check before you start
                        </h1>
                        <p className="mt-4 max-w-sm text-sm leading-6 text-indigo-50">
                            Verify your email so your workspace and outreach
                            activity stay connected to the right account.
                        </p>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-indigo-100">
                        Secure workspace access
                    </p>
                </div>

                <div className="w-full p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-md">
                        <div className="mb-6 text-center sm:mb-8">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 lg:hidden">
                                <Sparkles size={24} aria-hidden="true" />
                            </div>
                            <p className="text-sm font-medium text-indigo-600">
                                Check your inbox
                            </p>
                            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                                Verify Email
                            </h2>
                        </div>

                {error && (
                    <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-600">
                        {error}
                    </div>
                )}

                {resendOTP && (
                    <div className="mb-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
                        <CheckCircle2
                            size={18}
                            className="mt-0.5 shrink-0"
                            aria-hidden="true"
                        />
                        <p>Verification code sent to your email again.</p>
                    </div>
                )}

                {verifySuccess ? (
                    <div className="mb-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
                        <CheckCircle2
                            size={18}
                            className="mt-0.5 shrink-0"
                            aria-hidden="true"
                        />
                        <span>Email verified successfully!</span>
                    </div>
                ) : (
                    <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-center text-sm font-medium text-indigo-700">
                        Verification code has been sent to{" "}
                        <strong>{email}</strong>.
                    </div>
                )}
                <form onSubmit={handleVerify} noValidate className="space-y-4">
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">
                            Email ID
                        </label>
                        <div className="relative">
                            <Mail
                                size={18}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                aria-hidden="true"
                            />
                            <input
                                type="email"
                                readOnly
                                placeholder="you@example.com"
                                value={email}
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-10 py-3 text-sm text-slate-800 outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">
                            Verification Code
                        </label>
                        <div className="relative">
                            <KeyRound
                                size={18}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                aria-hidden="true"
                            />
                            <input
                                type="text"
                                placeholder="Enter verification code"
                                value={verificationCode}
                                onChange={(e) => {
                                    setVerificationCode(e.target.value);
                                    setVerifySuccess(false);
                                    setResendOTP(false);
                                    setError("");
                                }}
                                className="w-full rounded-xl border border-slate-200 bg-white px-10 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                            />
                        </div>
                        {codeSubmited && verificationCode.trim() === "" && (
                            <p className="mt-1.5 text-xs text-red-600">
                                Verification code is required.
                            </p>
                        )}
                    </div>
                    {verifySuccess ? (
                        <button
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                            onClick={() => {
                                navigate(ROUTES.LOGIN);
                                setResendOTP(false);
                            }}
                        >
                            <ArrowRight size={18} aria-hidden="true" />
                            Go to login
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={isVerifying}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                            onClick={() => {
                                setVerifySuccess(false);
                                setResendOTP(false);
                            }}
                        >
                            {isVerifying ? (
                                <Loader2
                                    size={18}
                                    className="animate-spin"
                                    aria-hidden="true"
                                />
                            ) : (
                                <ShieldCheck size={18} aria-hidden="true" />
                            )}
                            {isVerifying ? "Verifying..." : "Verify"}
                        </button>
                    )}

                    {!verifySuccess && (
                        <p className="text-center text-sm text-slate-600">
                            Didn't receive verification code?{" "}
                            <button
                                type="button"
                                onClick={() => handleResendVerificationOTP()}
                                className="inline-flex items-center gap-1 font-semibold text-indigo-600 transition hover:text-indigo-700"
                                disabled={verifySuccess || isResendingCode}
                            >
                                {isResendingCode ? (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <RotateCcw size={14} aria-hidden="true" />
                                )}
                                {isResendingCode
                                    ? "Sending..."
                                    : "Resend code"}
                            </button>
                        </p>
                    )}
                </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VerifyEmail;
